import { SummaryGrant } from "../../slices/grants";
import { TableProperties } from "exceljs";
import { 
  buildExcelTable, 
  genEmptyRow,
  getOrderedExportColumns,
  mapColumnsToExportFormat,
} from "../../services/exporter";
import { removeHtmlTags } from "../../services/dataParser";
import { DateFormat, formatDate } from "../../utils/conversion/date-converters";
import { ForecastRecord } from "../../utils/types/Forecast";
import axios from "axios";
import {
  createRequestWithAuthHeaders,
  getFullUrl,
} from "../../configs/axios-export.custom";
import { buildQuery } from "../../utils/Helpers/queryBuilder";
import { ECDRecord } from "../../slices/ecd";
import { format } from "date-fns";
import { useMemo } from "react";
import { ColumnsSettingsData, DataColumnConfigProps } from "../../components/MaterialTable/Table";
import { getECDColumnsConfig } from "./ecdColumns.config";

export const getSortableColumnPropertyName = (
  _propertyName: string,
): string => {
  return _propertyName;
};

export function parseRiskManagementConsentValue(
  value: string | null | undefined,
  options?: {
    parseForExport?: boolean;
  },
) {
  const { parseForExport } = options || {};
  if (parseForExport && value && !value.startsWith("RiskLOWMODERATEHIGH"))
    return value;
  if (!value || !value?.startsWith("RiskLOWMODERATEHIGH")) return null;
  const identified = value
    .split("Identified")[1]
    .split("Addressed")[0]
    .split("\n")
    .slice(0, 3);
  const addressed = value
    .split("Addressed")[1]
    .split("Note")[0]
    .split("\n")
    .slice(0, 3);
  const note = value.split("\r\n")[1];

  return parseForExport
    ? `Risks Identified: \n - Low ${identified[0] ?? "-"}\n - Moderate ${
        identified[1] ?? "-"
      }\n - High ${identified[2] ?? "-"}\nRisks Addressed: \n - Low ${
        addressed[0] ?? "-"
      }\n - Moderate ${addressed[1] ?? "-"}\n - High ${addressed[2] ?? "-"}${
        note ? `\nNote: ${note.trim()}` : ""
      }`
    : {
        identified,
        addressed,
        note: note ? note.trim() : null,
      };
}

export const getExportableDataTableECD = (
  data: ECDRecord[],
  savedColumnSettings?: ColumnsSettingsData[] | null,
): TableProperties => {
  // Get default columns configuration
  const defaultColumns = getECDColumnsConfig(getSortableColumnPropertyName);
  
  // Get ordered and filtered columns based on user settings
  const orderedColumns = getOrderedExportColumns(savedColumnSettings, defaultColumns);

  // Define data accessors for each column property
  const dataAccessors: { [propertyName: string]: (record: ECDRecord) => any } = {
    id: (record) => record.id,
    extECDRecord_Id: (record) => record.extECDRecord_Id,
    countries: (record) => (record.countries ?? []).join(", "),
    originatingBureauSubregionOffice: (record) => record.originatingBureauSubregionOffice,
    projectDateEnvironmentalDeterminationSignatureFyParsed: (record) => 
      record.projectDateEnvironmentalDeterminationSignatureFyParsed,
    sectorTypes: (record) => (record.sectorTypes ?? []).join(", "),
    documentTitle: (record) => record.documentTitle,
    documentType: (record) => record.documentType,
    fileNumber: (record) => record.fileNumber,
    projectDateActivityDateRangeStartParsed: (record) =>
      !record?.projectDateActivityDateRangeStartParsed &&
      !record?.projectDateActivityDateRangeEndParsed
        ? ""
        : `${
            record?.projectDateActivityDateRangeStartParsed
              ? format(
                  new Date(record.projectDateActivityDateRangeStartParsed),
                  "dd MMMM yyyy",
                )
              : "N/A"
          } - ${
            record?.projectDateActivityDateRangeEndParsed
              ? format(
                  new Date(record.projectDateActivityDateRangeEndParsed),
                  "dd MMMM yyyy",
                )
              : "N/A"
          }`,
    projectDateExpirationDateParsed: (record) =>
      !record.projectDateExpirationDateParsed
        ? ""
        : format(
            new Date(record.projectDateExpirationDateParsed),
            "dd MMMM yyyy",
          ),
    projectDateEnvironmentalDeterminationSignatureDateParsed: (record) =>
      !record.projectDateEnvironmentalDeterminationSignatureDateParsed
        ? ""
        : format(
            new Date(
              record.projectDateEnvironmentalDeterminationSignatureDateParsed,
            ),
            "dd MMMM yyyy",
          ),
    thresholdDetermination: (record) => (record.thresholdDetermination ?? []).join(", "),
    climateRiskManagementAssessment: (record) =>
      parseRiskManagementConsentValue(
        record.climateRiskManagementAssessment,
        { parseForExport: true },
      ),
    specificConditions: (record) => (record.specificConditions ?? []).join(", "),
  };

  // Map columns and data to export format
  const { columns, rows } = mapColumnsToExportFormat(orderedColumns, data, dataAccessors);

  return buildExcelTable("ECDExport", columns, rows);
};

export const getExcelDataECD = async (
  context,
  queryDto,
  totalItems: number = 1000,
): Promise<ECDRecord[]> => {
  const q = queryDto;
  if (q) {
    q.pageSize = totalItems;
    q.pageIndex = 0;
  }
  let result = Array<any>();
  await axios
    .get(
      getFullUrl(`/api/ecd${buildQuery(q)}`, {
        useDedicatedEnvironment: true,
      }),
      createRequestWithAuthHeaders(context),
    )
    .then((response) => {
      result = response.data.data;
    })
    .catch((error) => {
      alert("error fetching data");
    });
  return result;
};
